/**
 * ReviewSlider — DS-core review card (named tokens, no raw hex). Framer "Review
 * Card": [80px avatar] [name + quote] [up/down arrows], card stack with two dimmed
 * cards behind. Content-agnostic: `reviews` come in via props. Crossfade via
 * AnimatePresence + `m`; autoplays, pauses on hover, respects reduced motion.
 */
import { useEffect, useState } from "react";
import { m, useReducedMotion } from "motion/react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { EASE } from "./motion";

export type Review = {
  author: string;
  quote: string;
  avatar: string;
  alt?: string;
  role?: string;
};

export function ReviewSlider({ reviews, autoplayMs = 4500 }: { reviews: Review[]; autoplayMs?: number }) {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (reduce || paused || reviews.length < 2) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % reviews.length), autoplayMs);
    return () => clearInterval(id);
  }, [reduce, paused, reviews.length, autoplayMs]);

  if (reviews.length === 0) return null;
  const go = (dir: number) => setIndex((i) => (i + dir + reviews.length) % reviews.length);

  return (
    <div
      className="flex w-full items-center gap-3"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative flex min-w-0 flex-1">
        <span aria-hidden className="pointer-events-none absolute inset-0 translate-y-[14px] scale-[0.94] rounded-lg bg-white opacity-30 shadow-[0px_30px_19px_-20px_rgba(0,0,0,0.16)]" />
        <span aria-hidden className="pointer-events-none absolute inset-0 translate-y-[7px] scale-[0.97] rounded-lg bg-white opacity-30 shadow-[0px_30px_19px_-20px_rgba(0,0,0,0.16)]" />
        {/* Grid-stack: every review sits in the SAME grid cell (1/1), so the card
            always sizes to the TALLEST review and never resizes when it rotates —
            switching only crossfades opacity. No layout shift regardless of length. */}
        <div className="relative grid w-full rounded-lg bg-white p-4 shadow-[0px_30px_19px_-20px_rgba(0,0,0,0.16)] backdrop-blur-[80px]">
          {reviews.map((r, i) => (
            <m.figure
              key={i}
              aria-hidden={i !== index}
              style={{ gridArea: "1 / 1" }}
              initial={false}
              animate={reduce ? { opacity: i === index ? 1 : 0 } : { opacity: i === index ? 1 : 0, y: i === index ? 0 : 6 }}
              transition={{ duration: 0.4, ease: EASE }}
              className={`m-0 flex min-w-0 flex-row items-center gap-[14px] ${i === index ? "" : "pointer-events-none"}`}
            >
              <img
                src={r.avatar}
                alt={r.alt ?? r.author}
                loading="lazy" decoding="async"
                className="h-20 w-20 shrink-0 rounded-full object-cover"
              />
              <figcaption className="flex min-w-0 flex-col gap-1.5">
                <span className="font-ds text-ds-14 font-semibold text-black">
                  — {r.author}{r.role ? `, ${r.role}` : ""}
                </span>
                <p className="font-ds text-ds-14 leading-[1.5] text-ds-quote">{r.quote}</p>
              </figcaption>
            </m.figure>
          ))}
        </div>
      </div>

      <div className="flex w-4 shrink-0 flex-col items-center justify-center gap-2.5 text-white">
        <button aria-label="Previous review" onClick={() => go(-1)} className="text-white/70 transition-colors hover:text-white">
          <ChevronUp size={16} strokeWidth={2} />
        </button>
        <div className="flex flex-col items-center gap-1.5">
          {reviews.map((_, i) => (
            <button
              key={i}
              aria-label={`Review ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 w-1.5 rounded-full transition-colors ${i === index ? "bg-white" : "bg-white/40 hover:bg-white/70"}`}
            />
          ))}
        </div>
        <button aria-label="Next review" onClick={() => go(1)} className="text-white/70 transition-colors hover:text-white">
          <ChevronDown size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
